import { useState, useEffect } from 'react';
import { FormikProps } from 'formik';
import { agencies } from '../../../api/agencies';
import { offices } from '../../../api/offices';
import { useAuth } from '../../../contexts/AuthContext';
import { UserRole, User } from '../../../api/users';
import { AddUserFormValues } from '../types';
import { useActiveLeaderboardEvent } from '../../../hooks/useActiveLeaderboardEvent';

export const roleOptions = [
  { value: 'DIRECTOR', label: 'Agency Director' },
  { value: 'USER', label: 'Marketing department specialist' },
];

export const accessToOptions = [
  { value: 'marketing_hub', label: 'Marketing Hub' },
  { value: 'leaderboard', label: 'Leaderboard' },
];

export const useFormFields = (
  formik: FormikProps<AddUserFormValues>,
  userDetails?: User | null
) => {
  const { userData } = useAuth();
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;
  const [agencyOptions, setAgencyOptions] = useState<{ value: string; label: string }[]>([]);
  const [officeOptions, setOfficeOptions] = useState<{ value: string; label: string }[]>([]);
  const { activeEvent } = useActiveLeaderboardEvent();

  useEffect(() => {
    const fetchAgencies = async () => {
      if (userData?.role === UserRole.ADMIN) {
        try {
          const data = await agencies.getAll();
          const options = data.map((agency) => ({
            value: agency.id.toString(),
            label: agency.name,
          }));
          setAgencyOptions(options);
        } catch (error) {
          console.error('Failed to load agencies:', error);
        }
      } else {
        const options = userData?.agencies?.map((agency) => ({
          value: agency.id.toString(),
          label: agency.name,
        }));
        setAgencyOptions(options || []);
      }
    };

    fetchAgencies();
  }, [userData]);

  useEffect(() => {
    if (userData?.role !== UserRole.ADMIN) {
      setFieldValue('role', 'USER');
    }
  }, [setFieldValue, userData?.role]);

  useEffect(() => {
    const fetchOffices = async () => {
      if (!values.accessTo?.includes('leaderboard')) {
        setOfficeOptions([]);
        return;
      }

      try {
        if (!activeEvent?.id) return;
        const all = await offices.getByEventId(activeEvent.id);
        const options = all.map((office) => ({
          value: office.id.toString(),
          label: office.name,
        }));
        setOfficeOptions(options);

        if (userDetails?.leaderboardOffices && values.office.length === 0) {
          const userOfficeIds = userDetails.leaderboardOffices.map((o) => o.id.toString());
          const availableUserOffices = userOfficeIds.filter((id) =>
            options.some((o) => o.value === id)
          );
          if (availableUserOffices.length > 0) {
            setFieldValue('office', availableUserOffices);
          }
        } else if (values.office && values.office.length > 0) {
          const hasAny = values.office.some((id) => options.some((o) => o.value === id));
          if (!hasAny) setFieldValue('office', []);
        } else if (options.length === 1) {
          setFieldValue('office', [options[0].value]);
        }
      } catch (error) {
        console.error('Failed to load offices:', error);
        setOfficeOptions([]);
      }
    };

    fetchOffices();
  }, [
    values.accessTo,
    values.office,
    setFieldValue,
    activeEvent?.id,
    userDetails?.leaderboardOffices,
  ]);

  const availableAccessOptions =
    values.role === 'USER'
      ? accessToOptions
      : accessToOptions.filter((option) => option.value === 'leaderboard');

  const shouldShowOffice = values.accessTo?.includes('leaderboard') && values.role === 'USER';

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    agencyOptions,
    isAdmin: userData?.role === UserRole.ADMIN,
    availableAccessOptions,
    shouldShowOffice,
    officeOptions,
  };
};
